import * as React from "react";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createGRN } from "logic/permissions/GRN";
import { ConfirmDialog, IfPermitted } from "components/common";
import UserNotification from "util/UserNotification";
import { DeleteMenuItem } from "components/bootstrap";
import BulkActionsDropdown from "components/common/EntityDataTable/BulkActionsDropdown";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import { COLLECTIONS_QUERY_KEY } from "collections/hooks/useCollections";
import { COLLECTION_ENTITIES_QUERY_KEY } from "collections/hooks/useCollectionEntities";
import useCollectionEntityMutation from "collections/hooks/useCollectionEntityMutation";
import { useTranslation } from "react-i18next";
const BulkActions = ({ collectionId, selectedEntities }) => {
  const { t } = useTranslation("enterprise_collections");
  const { bulkRemoveEntitiesMutation } = useCollectionEntityMutation(collectionId);
  const queryClient = useQueryClient();
  const { setSelectedEntities } = useSelectedEntities();
  const [showDialog, setShowDialog] = useState(false);
  const selectedItemsAmount = selectedEntities == null ? void 0 : selectedEntities.length;
  const refetchCollection = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [COLLECTIONS_QUERY_KEY, collectionId]
    });
    queryClient.invalidateQueries({
      queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId]
    });
  }, [queryClient, collectionId]);
  const handleDelete = () => {
    setShowDialog(true);
  };
  const handleClearState = () => {
    setShowDialog(false);
    refetchCollection();
  };
  const onDelete = useCallback(() => {
    bulkRemoveEntitiesMutation.mutateAsync({ removedEntities: selectedEntities.map((entity) => createGRN(entity.type, entity.id)) }).then(({ failures }) => {
      if (failures == null ? void 0 : failures.length) {
        const notUpdatedCollectionEntityIds = failures.map(({ entity_id }) => entity_id);
        setSelectedEntities(notUpdatedCollectionEntityIds);
        UserNotification.error(
          t("collection_entities_bulk.remove_failed", {
            count: selectedItemsAmount,
            failed: notUpdatedCollectionEntityIds.length
          })
        );
      } else {
        setSelectedEntities([]);
        UserNotification.success(
          t("collection_entities_bulk.remove_success", { count: selectedItemsAmount }),
          t("shared.success")
        );
      }
    }).catch((error) => {
      UserNotification.error(t("collection_entities_bulk.remove_error", { error }));
    }).finally(() => {
      refetchCollection();
    });
  }, [bulkRemoveEntitiesMutation, refetchCollection, selectedEntities, selectedItemsAmount, setSelectedEntities, t]);
  const handleConfirm = () => {
    onDelete();
    setShowDialog(false);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(BulkActionsDropdown, null, /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `collection_entities:edit:${collectionId}` }, /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: handleDelete }, t("collection_entities_bulk.remove")))), showDialog && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      title: t("collection_entities_bulk.remove_title"),
      show: true,
      onConfirm: handleConfirm,
      onCancel: handleClearState
    },
    t("collection_entities_bulk.remove_confirm", { count: selectedItemsAmount })
  ));
};
export default BulkActions;
